import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const PLAN_LIMITS = {
  crm: { contacts: 1000, deals: 500, storage: 5 * 1024 * 1024 * 1024 },
  crm_voice: { contacts: 5000, deals: 2000, storage: 20 * 1024 * 1024 * 1024 },
  crm_voice_website: { contacts: 15000, deals: 8000, storage: 50 * 1024 * 1024 * 1024 },
  crm_voice_website_marketing: { contacts: 100000, deals: 50000, storage: 200 * 1024 * 1024 * 1024 },
  free: { contacts: 100, deals: 50, storage: 1 * 1024 * 1024 * 1024 },
};

const PLAN_NAMES = {
  crm: 'CRM',
  crm_voice: 'CRM + Voice',
  crm_voice_website: 'CRM + Voice + Website',
  crm_voice_website_marketing: 'Full Suite',
  free: 'Free Trial',
};

const NEXT_PLAN = {
  free: 'crm',
  crm: 'crm_voice',
  crm_voice: 'crm_voice_website',
  crm_voice_website: 'crm_voice_website_marketing',
  crm_voice_website_marketing: null,
};

const UsageDashboardWidget = () => {
  const { organization, profile } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState({ contacts: 0, deals: 0, storage: 0 });
  const [loading, setLoading] = useState(true);

  const planType = organization?.plan_type || 'free';
  const limits = PLAN_LIMITS?.[planType] || PLAN_LIMITS?.free;
  const nextPlan = NEXT_PLAN?.[planType];
  const nextPlanName = nextPlan ? PLAN_NAMES?.[nextPlan] : null;

  useEffect(() => {
    if (!profile?.org_id) return;
    const fetchUsage = async () => {
      setLoading(true);
      try {
        const [contactsRes, dealsRes] = await Promise.all([
          supabase?.from('contacts')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id),
          supabase?.from('deals')?.select('id', { count: 'exact', head: true })?.eq('org_id', profile?.org_id)?.is('deleted_at', null),
        ]);
        setUsage({
          contacts: contactsRes?.count || 0,
          deals: dealsRes?.count || 0,
          storage: 0,
        });
      } catch (err) {
        console.error('Usage fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
  }, [profile?.org_id]);

  const contactsPct = limits?.contacts > 0 ? Math.min(100, (usage?.contacts / limits?.contacts) * 100) : 0;
  const dealsPct = limits?.deals > 0 ? Math.min(100, (usage?.deals / limits?.deals) * 100) : 0;
  const storagePct = limits?.storage > 0 ? Math.min(100, (usage?.storage / limits?.storage) * 100) : 0;

  const isWarning = (pct) => pct >= 80 && pct < 90;
  const isCritical = (pct) => pct >= 90;
  const isAlert = (pct) => pct >= 80;

  const hasAnyAlert = isAlert(contactsPct) || isAlert(dealsPct) || isAlert(storagePct);
  const hasAnyCritical = isCritical(contactsPct) || isCritical(dealsPct) || isCritical(storagePct);

  const getBarColor = (pct) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 80) return 'bg-amber-500';
    return 'bg-primary';
  };

  const getTextColor = (pct) => {
    if (pct >= 90) return 'text-red-600 dark:text-red-400';
    if (pct >= 80) return 'text-amber-600 dark:text-amber-400';
    return 'text-muted-foreground';
  };

  const formatStorage = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb?.toFixed(1)} GB`;
  };

  const metrics = [
    {
      label: 'Contacts',
      icon: 'Users',
      used: usage?.contacts,
      limit: limits?.contacts,
      pct: contactsPct,
      display: `${usage?.contacts?.toLocaleString()} / ${limits?.contacts >= 100000 ? 'Unlimited' : limits?.contacts?.toLocaleString()}`,
    },
    {
      label: 'Deals',
      icon: 'Target',
      used: usage?.deals,
      limit: limits?.deals,
      pct: dealsPct,
      display: `${usage?.deals?.toLocaleString()} / ${limits?.deals >= 50000 ? 'Unlimited' : limits?.deals?.toLocaleString()}`,
    },
    {
      label: 'Storage',
      icon: 'HardDrive',
      used: usage?.storage,
      limit: limits?.storage,
      pct: storagePct,
      display: `${formatStorage(usage?.storage)} / ${formatStorage(limits?.storage)}`,
    },
  ];

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-muted animate-pulse rounded-lg" />
          <div className="h-5 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              <div className="h-2 bg-muted animate-pulse rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`bg-card border rounded-xl p-5 mb-6 ${hasAnyCritical ? 'border-red-300' : hasAnyAlert ? 'border-amber-300' : 'border-border'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${hasAnyCritical ? 'bg-red-100 dark:bg-red-900/30' : hasAnyAlert ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-primary/10'}`}>
            <Icon
              name={hasAnyCritical ? 'AlertTriangle' : hasAnyAlert ? 'AlertCircle' : 'BarChart2'}
              size={18}
              className={hasAnyCritical ? 'text-red-600 dark:text-red-400' : hasAnyAlert ? 'text-amber-600 dark:text-amber-400' : 'text-primary'}
            />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Plan Usage</h2>
            <p className="text-xs text-muted-foreground capitalize">{PLAN_NAMES?.[planType]} plan</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/billing')}
          className="text-xs text-primary hover:underline font-medium"
        >
          View billing →
        </button>
      </div>
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {metrics?.map((m) => (
          <div key={m?.label} className={`p-3 rounded-lg ${isAlert(m?.pct) ? (isCritical(m?.pct) ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30') : 'bg-muted/30'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name={m?.icon} size={14} className={getTextColor(m?.pct)} />
                <span className="text-xs font-medium text-foreground">{m?.label}</span>
              </div>
              {isAlert(m?.pct) && (
                <Icon
                  name={isCritical(m?.pct) ? 'AlertTriangle' : 'AlertCircle'}
                  size={13}
                  className={isCritical(m?.pct) ? 'text-red-500' : 'text-amber-500'}
                />
              )}
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-1.5 mb-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${getBarColor(m?.pct)}`}
                style={{ width: `${m?.pct}%` }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-medium ${getTextColor(m?.pct)}`}>{m?.pct?.toFixed(0)}% used</span>
              <span className="text-xs text-muted-foreground">{m?.display}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Warning / Upgrade Banner */}
      {hasAnyAlert && (
        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border ${hasAnyCritical ? 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800' : 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'}`}>
          <div className="flex items-start space-x-2">
            <Icon
              name={hasAnyCritical ? 'AlertTriangle' : 'AlertCircle'}
              size={15}
              className={`flex-shrink-0 mt-0.5 ${hasAnyCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}
            />
            <div>
              <p className={`text-xs font-semibold ${hasAnyCritical ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {hasAnyCritical ? 'You\'re almost out of space!' : 'Approaching plan limits'}
              </p>
              <p className={`text-xs mt-0.5 ${hasAnyCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {hasAnyCritical
                  ? 'Upgrade now to avoid service interruption and continue adding contacts & deals.'
                  : nextPlanName
                    ? `Upgrade to ${nextPlanName} for higher limits and more features.`
                    : 'Consider upgrading your plan for higher limits.'}
              </p>
            </div>
          </div>
          {nextPlan && (
            <button
              onClick={() => navigate('/billing')}
              className={`flex items-center justify-center space-x-1.5 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                hasAnyCritical
                  ? 'bg-red-600 text-white hover:bg-red-700' :'bg-amber-500 text-white hover:bg-amber-600'
              }`}
            >
              <Icon name="Zap" size={13} />
              <span>Upgrade to {nextPlanName}</span>
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default UsageDashboardWidget;
